/*
 * Commerce image workspace API layer (PlunkDocs specs/IMAGE_WORKSPACE.md §18, DS decisions §24).
 *
 * Every route is on /v1/content/images/* (AnalyticsService ImagesController, built in parallel with this UI).
 * Fail-soft rule (audiences/api.js precedent): a 404 / 405 / 501 means "not deployed yet" and resolves to null,
 * so the view shows the "not available yet" line instead of an error. Ids stay strings end to end (json-bigint).
 *
 * House rules baked in here: no credit amounts anywhere (the only credit message is the insufficient-credits
 * state with the purchase path), previews are free, no daily caps, no disclaimers, polling only (4 s).
 */
import endpoints from "../../endpoints";
import account from "../../data/account";
import { call, softFail, isSoft, ApiError, toast, isAdministrator } from "../audiences/api";
export { softFail, isSoft, ApiError, toast, isAdministrator };
const ROOT = String(endpoints.product && endpoints.product.root || "");
const IMAGES = `${ROOT}/content/images`;
const CONTENT = `${ROOT}/content`;
export const POLL_MS = 4000;
const idStr = v => v === undefined || v === null ? "" : String(v);
const items = d => Array.isArray(d) ? d : d && (d.items || d.Items) || [];

// ---- families ----------------------------------------------------------------

export const listImages = query => softFail(call(IMAGES, {
  query
})).then(items);
export const getImage = id => call(`${IMAGES}/${encodeURIComponent(idStr(id))}`);
export const createImage = body => call(IMAGES, {
  method: "POST",
  body
});
export const deleteImage = id => call(`${IMAGES}/${encodeURIComponent(idStr(id))}`, {
  method: "DELETE"
});

// ---- operations and jobs ----------------------------------------------------

export const runOp = (id, body) => call(`${IMAGES}/${encodeURIComponent(idStr(id))}/ops`, {
  method: "POST",
  body
});
export const getJob = jobId => call(`${IMAGES}/jobs/${encodeURIComponent(idStr(jobId))}`);

/**
 * Poll a job every 4 s (DS §24.17: polling only). Resolves {status, outputVersionId, error, job}; `isAlive()` stops
 * the loop when the view unmounts. A job that is still queued after `maxTries` resolves with status "timeout".
 */
export async function waitJob(jobId, isAlive, maxTries = 90) {
  for (let i = 0; i < maxTries && (!isAlive || isAlive()); i++) {
    await new Promise(res => setTimeout(res, POLL_MS));
    let j = null;
    try {
      j = await getJob(jobId);
    } catch (e) {
      if (e && e.status === 404 && i > 2) return {
        status: "failed",
        error: "The job was not found."
      };
      continue;
    }
    const status = String(j && (j.status || j.Status) || "").toLowerCase();
    if (status === "done" || status === "completed" || status === "complete" || status === "succeeded") {
      return {
        status: "done",
        outputVersionId: idStr(j.outputVersionId || j.versionId || j.rootId || j.imageId || j.id) || null,
        outputVersionIds: (j.outputVersionIds || j.versionIds || []).map(idStr),
        job: j
      };
    }
    if (status === "failed" || status === "error" || status === "cancelled") return {
      status: "failed",
      error: j && (j.error || j.message) || "The job failed.",
      job: j
    };
  }
  return {
    status: "timeout"
  };
}

// ---- autosave (free) --------------------------------------------------------

export const putText = (id, versionId, layers) => call(`${IMAGES}/${encodeURIComponent(idStr(id))}/text`, {
  method: "PUT",
  body: {
    versionId: idStr(versionId),
    layers
  }
});
export const putPlacements = (id, versionId, keys) => call(`${IMAGES}/${encodeURIComponent(idStr(id))}/placements`, {
  method: "PUT",
  body: {
    versionId: idStr(versionId),
    keys
  }
});

// ---- registry, categories, settings ----------------------------------------

export const getPlacements = () => softFail(call(`${IMAGES}/placements`)).then(d => d ? items(d) : null);
export const getCategories = () => softFail(call(`${IMAGES}/categories`)).then(d => d ? items(d) : null);
export const putCategoryMap = (leaf, category) => call(`${IMAGES}/categories/map`, {
  method: "PUT",
  body: {
    leaf,
    category
  },
  admin: true
});
/** GUESSED SHAPE: the recipe for a product before any image exists → {key,label,shotTypes[],scenes[],peopleDefault,placementsDefault[]}. */
export const resolveCategory = productId => softFail(call(`${IMAGES}/categories/resolve`, {
  query: {
    productId: idStr(productId)
  }
}));
/** GUESSED SHAPE: tenant switches → {people:{enabled}, approval:{required}}; absent = people off, approval off (DS §24.4/§24.6). */
export const getSettings = () => softFail(call(`${IMAGES}/settings`));

// ---- approve, hi-res, export ------------------------------------------------

export const getEstimate = (id, query) => softFail(call(`${IMAGES}/${encodeURIComponent(idStr(id))}/estimate`, {
  query
}));
export const approve = (id, versionId, state) => call(`${IMAGES}/${encodeURIComponent(idStr(id))}/approve`, {
  method: "POST",
  body: {
    versionId: idStr(versionId),
    state: state || "approved"
  }
});
export const makeHires = (id, versionId) => call(`${IMAGES}/${encodeURIComponent(idStr(id))}/hires`, {
  method: "POST",
  body: {
    versionId: idStr(versionId)
  }
});
export const exportImage = (id, body) => call(`${IMAGES}/${encodeURIComponent(idStr(id))}/export`, {
  method: "POST",
  body
});
export const getExports = (id, versionId) => softFail(call(`${IMAGES}/${encodeURIComponent(idStr(id))}/exports`, {
  query: {
    versionId: idStr(versionId)
  }
})).then(d => d ? items(d) : []);

// ---- assets: upload (upload-url → S3 PUT → POST assets, now with rights), product, library -------------------

export async function uploadAsset(file, {
  rights,
  rightsNote,
  language,
  onProgress,
  rejectSimilar
} = {}) {
  const pre = await call(`${CONTENT}/assets/upload-url`, {
    method: "POST",
    body: {
      fileName: file.name,
      contentType: file.type || "application/octet-stream",
      bytes: file.size
    }
  });
  if (!pre || !pre.uploadUrl) throw new Error(pre && pre.error || "Could not prepare the upload.");
  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", pre.uploadUrl, true);
    xhr.setRequestHeader("content-type", pre.contentType || file.type || "application/octet-stream");
    xhr.upload.onprogress = e => {
      if (e.lengthComputable && onProgress) onProgress(Math.round(e.loaded / e.total * 100));
    };
    xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status})`));
    xhr.onerror = () => reject(new Error("Upload failed (network)"));
    xhr.send(file);
  });
  const title = (file.name || "image").replace(/\.[a-z0-9]+$/i, "");
  return registerUpload({
    url: pre.url,
    title,
    rights,
    rightsNote,
    language,
    rejectSimilar,
    kind: pre.kind
  });
}

/**
 * Register an uploaded URL as a workspace root. Tries the workspace route first (dedupe on content hash, pHash and,
 * in Phase 2, image embeddings: `similar` carries {id, rootId?, title?, url?, score?, distance?}); while that route is
 * not deployed it falls back to the Phase 1 assets POST. `rejectSimilar` asks the server to create nothing when a close
 * match exists, so the view can show "Looks like N images you already have" before anything is written.
 */
export async function registerUpload({
  url,
  title,
  rights,
  rightsNote,
  language,
  rejectSimilar,
  kind,
  productId,
  source
} = {}) {
  const body = {
    url,
    title,
    source: source || "upload",
    rights: rights || "own",
    rightsNote: rightsNote || undefined,
    language: language || "en",
    rejectSimilar: rejectSimilar ? true : undefined,
    productId: productId || undefined
  };
  let a = await softFail(call(`${IMAGES}/register`, {
    method: "POST",
    body
  }));
  if (a === null) a = await call(`${CONTENT}/assets`, {
    method: "POST",
    body: {
      kind: kind || "image",
      ...body,
      rejectSimilar: undefined
    }
  });
  const v = a && a.version || a || {};
  const similar = (a && a.similar || []).map(x => ({
    id: idStr(x.id || x.rootId),
    rootId: idStr(x.rootId || x.id),
    title: x.title || "",
    url: x.url || "",
    score: x.score != null ? Number(x.score) : null,
    distance: x.distance != null ? Number(x.distance) : null,
    approvalState: x.approvalState || ""
  }));
  const created = a && a.created === false ? false : !!(v && (v.id || v.Id));
  return {
    id: created ? idStr(v.id || v.Id) : "",
    url,
    duplicate: !!(a && a.duplicate),
    of: a && a.of ? idStr(a.of) : null,
    similar,
    created
  };
}

/** The product behind an asset (exists today): {products:[{id,title,productType,category,options:{Color:[…]}}], brand}. */
export const assetProduct = assetId => softFail(call(`${CONTENT}/assets/${encodeURIComponent(idStr(assetId))}/product`));

/** Library images (roots) for the first-run picker and the references grid. */
export const listLibraryImages = (q, top = 48) => softFail(call(`${CONTENT}/assets`, {
  query: {
    kind: "image",
    q,
    $top: top
  }
})).then(d => items(d).filter(a => a && a.url && !a.parentAssetId).map(a => ({
  id: idStr(a.id),
  url: a.latestUrl || a.url,
  title: a.title || "",
  source: a.source || "",
  hasFaces: a.hasFaces === true || a.hasFaces === 1,
  altText: a.altText || ""
})));

/** Catalog search for the product anchor (the compose endpoint that already serves the email composer). */
export const searchProducts = (q, top = 12) => softFail(call(`${ROOT}/campaign/email/compose/products`, {
  query: {
    q,
    $top: top
  }
})).then(d => items(d).map(p => ({
  id: idStr(p.id),
  title: p.title || "",
  imageUrl: p.imageUrl || "",
  url: p.url || "",
  productType: p.productType || p.category || "",
  source: p.source || ""
})));

// ---- Phase 2: Shopify round trip ------------------------------------------------------------
// GET → { connected, shop, product {gid, legacyId, handle, title, url, variants[{gid, legacyId, title, options[{name,value}]}],
//         media[{gid, position, alt, url, mediaContentType, variantGids[]}]}, rows[{assetId, direction, mediaGid, variantGid,
//         position, alt, filename, status, error, syncedDate}] }. null while the route is not deployed.
export const getShopify = id => softFail(call(`${IMAGES}/${encodeURIComponent(idStr(id))}/shopify`)).then(d => {
  if (!d) return null;
  const product = d.product ? {
    ...d.product,
    gid: idStr(d.product.gid),
    legacyId: idStr(d.product.legacyId),
    variants: (d.product.variants || []).map(v => ({
      ...v,
      gid: idStr(v.gid),
      legacyId: idStr(v.legacyId),
      options: v.options || []
    })),
    media: (d.product.media || []).map(m => ({
      ...m,
      gid: idStr(m.gid),
      position: Number(m.position) || 0,
      variantGids: (m.variantGids || []).map(idStr)
    }))
  } : null;
  const rows = (d.rows || []).map(r => ({
    ...r,
    assetId: idStr(r.assetId),
    mediaGid: idStr(r.mediaGid),
    variantGid: idStr(r.variantGid),
    position: r.position != null ? Number(r.position) : null
  }));
  return {
    connected: !!d.connected,
    shop: d.shop || "",
    product,
    rows
  };
});
/** POST { versionId, productId?, variantGid?, position?, replaceMediaGid?, alt, filename } → { mediaGid, position, productUrl, version }; 409 hires_required | approval_required | no_connector. */
export const pushShopify = (id, body) => call(`${IMAGES}/${encodeURIComponent(idStr(id))}/shopify`, {
  method: "POST",
  body
});

// ---- Phase 2: colorway sets ---------------------------------------------------------------
/** POST { versionId, attribute?, values?[], placements?[] } → 202 { batchId, jobs[{jobId, value}] }. */
export const startColorways = (id, body) => call(`${IMAGES}/${encodeURIComponent(idStr(id))}/colorways`, {
  method: "POST",
  body
});
/** GET → { batchId, done, jobs[{jobId, status, value, outputVersionId, fidelity, error}] }. */
export const getBatch = (id, batchId) => call(`${IMAGES}/${encodeURIComponent(idStr(id))}/batches/${encodeURIComponent(idStr(batchId))}`).then(b => b ? {
  ...b,
  batchId: idStr(b.batchId || batchId),
  done: !!b.done,
  jobs: (b.jobs || []).map(j => ({
    ...j,
    jobId: idStr(j.jobId || j.id),
    outputVersionId: j.outputVersionId ? idStr(j.outputVersionId) : null,
    status: String(j.status || "queued").toLowerCase()
  }))
} : null);
/** Approve (or reject, or send back to draft) a whole colorway set: POST approve with batchId instead of versionId. */
export const approveBatch = (id, batchId, state, note, override) => call(`${IMAGES}/${encodeURIComponent(idStr(id))}/approve`, {
  method: "POST",
  body: {
    batchId: idStr(batchId),
    state: state || "approved",
    note: note || undefined,
    override: override || undefined
  }
});
export const deleteVersion = id => call(`${IMAGES}/${encodeURIComponent(idStr(id))}`, {
  method: "DELETE",
  query: {
    scope: "version"
  }
});

// ---- Phase 2: text per language, fonts -----------------------------------------------------
/** POST { versionId, languages[] } → { textLayers } (the same shape the PUT takes; text.{lang} filled per layer). */
export const translateText = (id, versionId, languages) => call(`${IMAGES}/${encodeURIComponent(idStr(id))}/translate`, {
  method: "POST",
  body: {
    versionId: idStr(versionId),
    languages
  }
});
/** GET → { items[{id, family, license, licenseNote, allowedUses, fileUrl, createdBy, createdDate}], system[string] }; null while not deployed. */
export const getFonts = () => softFail(call(`${IMAGES}/fonts`)).then(d => d ? {
  items: items(d).map(f => ({
    ...f,
    id: idStr(f.id)
  })),
  system: Array.isArray(d.system) ? d.system : []
} : null);
export async function uploadFont(file, {
  family,
  license,
  licenseNote,
  onProgress
} = {}) {
  const pre = await call(`${IMAGES}/fonts/upload-url`, {
    method: "POST",
    body: {
      filename: file.name,
      contentType: file.type || fontContentType(file.name)
    }
  });
  const url = pre && (pre.url || pre.uploadUrl);
  if (!url) throw new Error(pre && pre.error || "Could not prepare the font upload.");
  await putFile(url, file, file.type || fontContentType(file.name), onProgress);
  const row = await call(`${IMAGES}/fonts`, {
    method: "POST",
    body: {
      family,
      key: pre.key,
      license: license || "unknown",
      licenseNote: licenseNote || undefined
    }
  });
  return row ? {
    ...row,
    id: idStr(row.id)
  } : row;
}
export const deleteFont = id => call(`${IMAGES}/fonts/${encodeURIComponent(idStr(id))}`, {
  method: "DELETE"
});
function fontContentType(name) {
  const ext = String(name || "").split(".").pop().toLowerCase();
  return ext === "woff2" ? "font/woff2" : ext === "otf" ? "font/otf" : "font/ttf";
}

// ---- Phase 2: masks, similar, stats ------------------------------------------------------------
/** Upload a painted mask PNG (white = edit, black = keep, the version's pixel size) through the assets presign; returns the S3 key for `maskKey`. */
export async function uploadMask(blob, name = "mask.png") {
  const pre = await call(`${CONTENT}/assets/upload-url`, {
    method: "POST",
    body: {
      fileName: name,
      contentType: "image/png",
      bytes: blob.size
    }
  });
  if (!pre || !pre.uploadUrl) throw new Error(pre && pre.error || "Could not prepare the mask upload.");
  await putFile(pre.uploadUrl, blob, "image/png");
  return pre.key || pre.objectKey || keyFromUrl(pre.url);
}
/** "https://plunk-video.s3.amazonaws.com/uploads/t/x.png" | "https://s3.../plunk-video/uploads/t/x.png" → "uploads/t/x.png". */
export function keyFromUrl(u) {
  try {
    const url = new URL(u);
    let path = url.pathname.replace(/^\/+/, "");
    if (/^plunk-video\//.test(path)) path = path.replace(/^plunk-video\//, "");
    return decodeURIComponent(path.split("?")[0]);
  } catch (e) {
    return String(u || "");
  }
}
/** GET similar?top=8 → [{id, rootId, title, productId, score, url, approvalState}]; null while not deployed. */
export const getSimilar = (id, top = 8) => softFail(call(`${IMAGES}/${encodeURIComponent(idStr(id))}/similar`, {
  query: {
    top
  }
})).then(d => d ? items(d).map(x => ({
  id: idStr(x.id),
  rootId: idStr(x.rootId || x.id),
  title: x.title || "",
  productId: x.productId ? idStr(x.productId) : "",
  score: x.score != null ? Number(x.score) : null,
  url: x.url || "",
  approvalState: x.approvalState || ""
})) : null);
/** GET stats?range=30d|90d|12m → { range, days[{date, sessions, conversions, conversionValue}], totals{...} }; null while not deployed. */
export const getStats = (id, range = "30d") => softFail(call(`${IMAGES}/${encodeURIComponent(idStr(id))}/stats`, {
  query: {
    range
  }
})).then(d => d ? {
  range: d.range || range,
  days: d.days || [],
  totals: {
    sessions: Number((d.totals || {}).sessions) || 0,
    conversions: Number((d.totals || {}).conversions) || 0,
    conversionValue: Number((d.totals || {}).conversionValue) || 0
  }
} : null);
function putFile(url, body, contentType, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    xhr.setRequestHeader("content-type", contentType || "application/octet-stream");
    xhr.upload.onprogress = e => {
      if (e.lengthComputable && onProgress) onProgress(Math.round(e.loaded / e.total * 100));
    };
    xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status})`));
    xhr.onerror = () => reject(new Error("Upload failed (network)"));
    xhr.send(body);
  });
}

/** Common language codes for the Text panel's "Add language" picker (base language first in the UI). */
export const LANGUAGES = [["en", "English"], ["es", "Spanish"], ["fr", "French"], ["de", "German"], ["it", "Italian"], ["pt", "Portuguese"], ["nl", "Dutch"], ["sv", "Swedish"], ["da", "Danish"], ["nb", "Norwegian"], ["fi", "Finnish"], ["pl", "Polish"], ["cs", "Czech"], ["tr", "Turkish"], ["el", "Greek"], ["ru", "Russian"], ["ar", "Arabic"], ["he", "Hebrew"], ["hi", "Hindi"], ["ja", "Japanese"], ["ko", "Korean"], ["zh", "Chinese"], ["th", "Thai"], ["vi", "Vietnamese"], ["id", "Indonesian"]];
export const langName = code => {
  const hit = LANGUAGES.find(([k]) => k === String(code || "").toLowerCase());
  return hit ? hit[1] : String(code || "").toUpperCase();
};

/** SEO filename for a Shopify push: `{brand}-{handle}-{variant-value}-{shot}.jpg`, lowercased ASCII, 80 max (DS §24.9). */
export function seoFilename(parts, ext = "jpg") {
  const slug = v => String(v || "").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  const base = parts.map(slug).filter(Boolean).join("-").replace(/-{2,}/g, "-").slice(0, 80 - ext.length - 1).replace(/-+$/, "");
  return `${base || "image"}.${ext}`;
}
export function cleanFilename(v, ext = "jpg") {
  const raw = String(v || "").toLowerCase().replace(/\.(jpe?g|png|webp)$/i, "");
  const base = raw.normalize("NFKD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9-]+/g, "-").replace(/-{2,}/g, "-").replace(/^-+/, "").slice(0, 80 - ext.length - 1);
  return `${base}.${ext}`;
}

// ---- tenant facts -----------------------------------------------------------

export function hasBothApps() {
  const a = account.get() || {};
  const apps = a.apps || [];
  return apps.indexOf("studio") !== -1 && apps.indexOf("app") !== -1;
}
export function studioUrl() {
  return window.location.hostname === "staging.plunk.io" ? "https://studio.staging.plunk.io" : "https://studio.plunk.io";
}
export function currentUser() {
  const a = account.get() || {};
  return a.email || a.userName || a.name || "";
}

// ---- pure helpers shared by the view --------------------------------------------

/** "9:16" | "1.91:1" | 0.5625 → number (width / height). */
export function aspectNum(a) {
  if (typeof a === "number") return a;
  const s = String(a || "1:1").split(":");
  const w = parseFloat(s[0]),
    h = parseFloat(s[1] || "1");
  return w > 0 && h > 0 ? w / h : 1;
}

/** Normalise a registry safe zone: inset {t,r,b,l} fractions, clear [{side, pct}] fractions. */
export function safeZone(p) {
  const s = p && p.safe || {};
  const frac = v => (v > 1 ? v / 100 : v) || 0;
  let inset = s.inset;
  if (typeof inset === "number" || typeof inset === "string") {
    const f = frac(parseFloat(inset));
    inset = {
      t: f,
      r: f,
      b: f,
      l: f
    };
  } else if (inset && typeof inset === "object") inset = {
    t: frac(inset.t ?? inset.top ?? 0),
    r: frac(inset.r ?? inset.right ?? 0),
    b: frac(inset.b ?? inset.bottom ?? 0),
    l: frac(inset.l ?? inset.left ?? 0)
  };else inset = {
    t: 0,
    r: 0,
    b: 0,
    l: 0
  };
  const clear = (s.clear || []).map(c => ({
    side: String(c.side || "").toLowerCase(),
    pct: frac(parseFloat(c.pct ?? c.size ?? 0)),
    x: c.x,
    y: c.y,
    w: c.w,
    h: c.h
  }));
  // clear bands eat into the inset on their side
  clear.forEach(c => {
    if (c.side === "top") inset.t = Math.max(inset.t, c.pct);
    if (c.side === "bottom") inset.b = Math.max(inset.b, c.pct);
    if (c.side === "left") inset.l = Math.max(inset.l, c.pct);
    if (c.side === "right") inset.r = Math.max(inset.r, c.pct);
  });
  return {
    inset,
    clear
  };
}

/** Centre crop window (fractions of the source) that covers a target aspect. */
export function cropWindow(srcW, srcH, targetAspect) {
  const sa = srcW > 0 && srcH > 0 ? srcW / srcH : 1;
  const ta = aspectNum(targetAspect);
  if (sa > ta) {
    const cw = ta / sa;
    return {
      x: (1 - cw) / 2,
      y: 0,
      w: cw,
      h: 1
    };
  }
  const ch = sa / ta;
  return {
    x: 0,
    y: (1 - ch) / 2,
    w: 1,
    h: ch
  };
}

/**
 * Does the product box (fractions of the source image) sit inside the placement's safe zone after the centre crop?
 * Without a product box the rule is the aspect itself: a crop that loses more than 12% of the frame needs an extend.
 */
export function placementFit(version, placement) {
  const w = Number(version && version.width) || 1,
    h = Number(version && version.height) || 1;
  const win = cropWindow(w, h, placement.aspect);
  const {
    inset
  } = safeZone(placement);
  const box = version && version.productBox;
  if (box && box.w > 0 && box.h > 0) {
    const safe = {
      x: win.x + win.w * inset.l,
      y: win.y + win.h * inset.t,
      w: win.w * (1 - inset.l - inset.r),
      h: win.h * (1 - inset.t - inset.b)
    };
    const fits = box.x >= safe.x - 0.005 && box.y >= safe.y - 0.005 && box.x + box.w <= safe.x + safe.w + 0.005 && box.y + box.h <= safe.y + safe.h + 0.005;
    return {
      fits,
      lost: 1 - win.w * win.h
    };
  }
  const lost = 1 - win.w * win.h;
  return {
    fits: lost <= 0.12,
    lost
  };
}

/** Fallback recipes (spec §8.2) used only while GET /images/categories is not deployed. */
export const FALLBACK_RECIPES = [{
  key: "furniture_home",
  label: "Furniture and home",
  shotTypes: ["hero", "room_scene", "detail", "silhouette"],
  scenes: ["room", "studio", "outdoor"],
  peopleDefault: false,
  placementsDefault: ["shopify_pdp", "instagram_feed", "email_hero"],
  keywords: /sofa|sectional|loveseat|chair|table|dresser|lamp|rug|bed|cabinet|furniture/i
}, {
  key: "apparel",
  label: "Apparel",
  shotTypes: ["on_model", "flat_lay", "detail", "ghost"],
  scenes: ["flat_lay", "on_model", "studio", "lifestyle"],
  peopleDefault: true,
  placementsDefault: ["shopify_pdp", "instagram_feed", "instagram_story"],
  keywords: /shirt|dress|jacket|pant|hoodie|tee|skirt|coat|clothing/i
}, {
  key: "footwear_accessories",
  label: "Footwear and accessories",
  shotTypes: ["hero", "on_model", "detail", "pair"],
  scenes: ["studio", "on_model", "flat_lay", "outdoor"],
  peopleDefault: true,
  placementsDefault: ["shopify_pdp", "instagram_feed", "google_demand_gen"],
  keywords: /shoe|sneaker|boot|bag|belt|wallet|watch|sunglass|handbag|jewelry/i
}, {
  key: "beauty",
  label: "Beauty",
  shotTypes: ["hero", "texture", "swatch", "in_hand"],
  scenes: ["studio", "flat_lay", "in_hand", "shelf"],
  peopleDefault: true,
  placementsDefault: ["shopify_pdp", "instagram_story", "instagram_feed"],
  keywords: /serum|cream|lipstick|shampoo|fragrance|mascara|cleanser|moisturi|beauty/i
}, {
  key: "food_beverage",
  label: "Food and beverage",
  shotTypes: ["plated", "ingredient", "packaging", "in_hand"],
  scenes: ["kitchen", "flat_lay", "studio", "outdoor"],
  peopleDefault: false,
  placementsDefault: ["shopify_pdp", "instagram_feed", "facebook_feed"],
  keywords: /coffee|tea|snack|sauce|wine|chocolate|granola|food|beverage/i
}, {
  key: "electronics",
  label: "Electronics",
  shotTypes: ["hero", "in_use", "ports", "detail"],
  scenes: ["desk", "studio", "in_hand", "lifestyle"],
  peopleDefault: false,
  placementsDefault: ["shopify_pdp", "google_demand_gen", "site_hero"],
  keywords: /headphone|speaker|charger|laptop|camera|phone|monitor|electronic/i
}, {
  key: "toys_hobby",
  label: "Toys and hobby",
  shotTypes: ["hero", "in_play", "detail", "box"],
  scenes: ["flat_lay", "studio", "room", "outdoor"],
  peopleDefault: true,
  placementsDefault: ["shopify_pdp", "instagram_feed", "facebook_feed"],
  keywords: /toy|puzzle|plush|board game|lego|games/i
}, {
  key: "sports_outdoor",
  label: "Sports and outdoor",
  shotTypes: ["hero", "in_use", "detail", "flat_lay"],
  scenes: ["outdoor", "studio", "flat_lay", "on_model"],
  peopleDefault: true,
  placementsDefault: ["shopify_pdp", "instagram_story", "google_demand_gen"],
  keywords: /yoga|bike|tent|fitness|golf|kayak|sporting/i
}, {
  key: "pets",
  label: "Pets",
  shotTypes: ["hero", "in_use", "detail"],
  scenes: ["room", "outdoor", "studio", "flat_lay"],
  peopleDefault: false,
  placementsDefault: ["shopify_pdp", "instagram_feed", "facebook_feed"],
  keywords: /\bdog|\bcat\b|\bpet|leash|litter/i
}, {
  key: "general",
  label: "General",
  shotTypes: ["hero", "detail", "in_context"],
  scenes: ["studio", "lifestyle", "flat_lay"],
  peopleDefault: false,
  placementsDefault: ["shopify_pdp", "instagram_feed"],
  keywords: /$^/
}];

/** Fallback registry (spec §11) used only while GET /images/placements is not deployed. */
export const FALLBACK_PLACEMENTS = [{
  key: "shopify_pdp",
  channel: "shopify",
  name: "Shopify product page",
  width: 2048,
  height: 2048,
  aspect: "1:1",
  safe: {
    inset: 0.05
  },
  textAllowed: false,
  format: "jpg",
  caption: false
}, {
  key: "instagram_story",
  channel: "instagram",
  name: "Instagram story",
  width: 1080,
  height: 1920,
  aspect: "9:16",
  safe: {
    inset: {
      t: 0,
      r: 0.05,
      b: 0,
      l: 0.05
    },
    clear: [{
      side: "top",
      pct: 0.14
    }, {
      side: "bottom",
      pct: 0.2
    }]
  },
  textAllowed: true,
  format: "jpg",
  caption: false
}, {
  key: "instagram_feed",
  channel: "instagram",
  name: "Instagram feed",
  width: 1080,
  height: 1350,
  aspect: "4:5",
  safe: {
    inset: 0.04
  },
  textAllowed: true,
  format: "jpg",
  caption: true
}, {
  key: "facebook_feed",
  channel: "facebook",
  name: "Facebook feed",
  width: 1200,
  height: 628,
  aspect: "1.91:1",
  safe: {
    inset: 0.05
  },
  textAllowed: true,
  format: "jpg",
  caption: true
}, {
  key: "google_demand_gen",
  channel: "google_ads",
  name: "Google Demand Gen",
  width: 1200,
  height: 628,
  aspect: "1.91:1",
  safe: {
    inset: 0.05,
    clear: [{
      side: "bottom-right",
      pct: 0.18
    }]
  },
  textAllowed: true,
  format: "jpg",
  caption: true
}, {
  key: "email_hero",
  channel: "email",
  name: "Email hero",
  width: 1200,
  height: 600,
  aspect: "2:1",
  safe: {
    inset: 0.05,
    clear: [{
      side: "bottom",
      pct: 0.5
    }]
  },
  textAllowed: true,
  format: "jpg",
  caption: true
}, {
  key: "site_hero",
  channel: "site",
  name: "Site hero",
  width: 1920,
  height: 800,
  aspect: "12:5",
  safe: {
    inset: 0,
    clear: [{
      side: "left",
      pct: 0.4
    }]
  },
  textAllowed: false,
  format: "webp",
  caption: false
}];

/** Normalise a recipe row from the API or the fallback. */
export function normRecipe(r) {
  if (!r) return null;
  const list = v => Array.isArray(v) ? v : typeof v === "string" ? (() => {
    try {
      return JSON.parse(v);
    } catch (e) {
      return v.split(",").map(x => x.trim()).filter(Boolean);
    }
  })() : [];
  return {
    key: r.key || r.category || "general",
    label: r.label || r.key || "General",
    shotTypes: list(r.shotTypes || r.shot_types || r.shotTypesJson),
    scenes: list(r.scenes || r.scenePresets || r.scene_presets),
    peopleDefault: !!(r.peopleDefault ?? r.people_default),
    placementsDefault: list(r.placementsDefault || r.placements_default || r.placements),
    keywords: r.keywords instanceof RegExp ? r.keywords : null
  };
}

/** Client-side category guess from the product's type and title (the §8.2 keyword rules), for the anchor card while the server resolve is not deployed. */
export function guessCategory(recipes, product) {
  const text = `${product && product.productType || ""} ${product && product.title || ""}`;
  const hit = (recipes || []).find(r => r.keywords && r.keywords.test(text));
  return hit || (recipes || []).find(r => r.key === "general") || normRecipe(FALLBACK_RECIPES[FALLBACK_RECIPES.length - 1]);
}